import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";
import { extractRequestFromQueryKey } from "../../../keys";
import { TellescopeModelFromName } from "../../helper-types";
import { updateModelWithOptions } from "../../../utils";

export const ENDUSERS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"endusers"> = {
  updateOne: (mutationRequest, mutationResponse) => ({
    check_eligibility: {
      exact: false,
      request: {
        id: mutationResponse?.id ?? mutationRequest.id,
      },
      updater: (oldData) => {
        if (!oldData) return oldData;
        if (mutationResponse) return { ...oldData, enduser: mutationResponse };
        const { _options, ...updates } = mutationRequest;
        return { ...oldData, enduser: updateModelWithOptions(oldData.enduser, updates, _options) };
      },
    },
    is_authenticated: {
      exact: false,
      request: {
        id: mutationResponse?.id ?? mutationRequest.id,
      },
      updater: (oldData) => {
        if (!oldData) return oldData;
        if (mutationResponse)
          return {
            ...oldData,
            enduser: mutationResponse,
          };
        const { _options, ...updates } = mutationRequest;
        return {
          ...oldData,
          enduser: updateModelWithOptions(oldData.enduser, updates, _options),
        };
      },
    },
    related_contacts_report: undefined, // Irrelevant to update based on enduser mutation
    load_inbox_data: {
      exact: false,
      predicate: ({ state: { data }, queryKey }) => {
        const isInList = data?.pages.some((page) =>
          page.endusers.some((enduser) => enduser.id === (mutationResponse?.id ?? mutationRequest.id))
        );
        if (isInList) return true;
        const newPhone = mutationResponse?.phone ?? mutationRequest.phone;
        if (!newPhone) return false;
        const request = extractRequestFromQueryKey(queryKey);
        return request?.phoneNumber
          ? request.phoneNumber.replace(/[^0-9]/, "") === newPhone.replace(/[^0-9]/, "")
          : false;
      },
      updater: (oldData, context) => {
        const queryKey = context?.queryKey;
        if (!oldData || !queryKey) return oldData;
        let existingEnduser: TellescopeModelFromName<"endusers"> | undefined = undefined;
        const isInList = oldData.pages.some((page) =>
          page.endusers.some((enduser) => {
            if (enduser.id === (mutationResponse?.id ?? mutationRequest.id)) {
              existingEnduser = enduser;
              return true;
            }
          })
        );

        if (!existingEnduser && !mutationResponse) {
          return oldData;
        }

        const typedExistingEnduser = existingEnduser as TellescopeModelFromName<"endusers"> | undefined;

        const { _options, ...updates } = mutationRequest;
        const updatedUser = typedExistingEnduser
          ? mutationResponse ?? updateModelWithOptions(typedExistingEnduser, updates, _options)
          : mutationResponse!;

        const updatedPhone = updatedUser.phone && updatedUser.phone.replace(/[^0-9]/, "");
        const oldPhone = typedExistingEnduser?.phone && typedExistingEnduser.phone.replace(/[^0-9]/, "");
        const phoneHasChanged = updatedPhone !== oldPhone;

        const request = extractRequestFromQueryKey(queryKey);
        const shouldBeInList = isInList && request?.phoneNumber ? !phoneHasChanged : true;

        if (shouldBeInList) {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              endusers: page.endusers.map((enduser) => (enduser.id === updatedUser.id ? updatedUser : enduser)),
            })),
          };
        }
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            endusers: [...page.endusers].filter((enduser) => enduser.id !== updatedUser.id),
          })),
        };
      },
    },
    get_journeys_report: undefined, // Irrelevant to update based on enduser mutation
    get_report: undefined, // Irrelevant to update based on enduser mutation
    get_engagement_statistics: undefined, // Irrelevant to update based on enduser mutation
    generate_auth_token: undefined, // Irrelevant to update based on enduser mutation
    get_engagement_statistics_by_userId: undefined, // Irrelevant to update based on enduser mutation
  }),
};
